// AI Automation landing-page data — drives /{industry}-ai-automation pages via
// AiAutomationPage.jsx. Each entry is industry-specific (use-cases, ROI stats,
// case study, FAQ). Shared scaffolding (deployment process, generic FAQ, CTA)
// lives in the template, not here.
//
// Each entry:
//   aiSlug         the page URL slug (e.g. "real-estate-ai-automation")
//   industrySlug   matching slug in industryData.js (for name/emoji/cover/related)
//   metaTitle, metaDescription
//   heroEyebrow, heroHeadline, heroAccent, heroTagline
//   heroStats[3]   { value, label }
//   insightStats[3] { value, label, desc }   — the "why" / ROI section
//   automations[6] { title, body }            — industry-specific AI workflows
//   stack[]        integration / tool chips
//   caseStudy      { metric, metricLabel, badge, title, body }
//   faq[3..4]      { q, a }

export const AI_AUTOMATION_LIST = [
  /* ───────────────────────── SHOPIFY & ECOMMERCE ───────────────────────── */
  {
    aiSlug: "ecommerce-ai-automation",
    industrySlug: "shopify-ecommerce",
    metaTitle:
      "Ecommerce AI Automation — Support Bots, Product Recs & Win-Back AI | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for ecommerce & Shopify brands: 24/7 support agents, AI product recommendations, abandoned-cart recovery and review automation. Free automation audit.",
    heroEyebrow: "Ecommerce AI Automation",
    heroHeadline: "AI That Sells, Supports and Retains — On Autopilot",
    heroAccent: "More revenue per visitor. Less manual work.",
    heroTagline:
      "We deploy AI agents and automations across your store — support, product discovery, cart recovery and post-purchase — so every shopper gets an instant, personalised experience and your team stops drowning in tickets.",
    heroStats: [
      { value: "24/7", label: "AI support coverage" },
      { value: "−68%", label: "support tickets handled by humans" },
      { value: "+22%", label: "recovered cart revenue" },
    ],
    insightStats: [
      {
        value: "73%",
        label: "of shoppers expect instant answers",
        desc: "Slow replies kill conversions. An AI agent answers sizing, shipping and stock questions in seconds — at any hour — so buyers don't bounce to a competitor.",
      },
      {
        value: "$18",
        label: "saved per automated support ticket",
        desc: "AI resolves the repetitive 60–70% of tickets instantly, freeing your team for high-value conversations and slashing support cost per order.",
      },
      {
        value: "10–30%",
        label: "of revenue recoverable from carts",
        desc: "AI-personalised cart and browse-abandon sequences recover sales that flat email blasts never touch.",
      },
    ],
    automations: [
      {
        title: "24/7 AI Support Agent",
        body: "A trained agent on your site, WhatsApp and email that answers product, shipping, returns and order-status questions instantly — escalating only the edge cases to a human.",
      },
      {
        title: "AI Product Recommendations",
        body: "Conversational discovery that asks the right questions and recommends the perfect product, lifting average order value and turning browsers into buyers.",
      },
      {
        title: "Abandoned-Cart & Browse Recovery",
        body: "AI-personalised SMS and email sequences that reference the exact product, objection and discount most likely to bring each shopper back.",
      },
      {
        title: "Order-Status & Returns Automation",
        body: "Customers self-serve tracking, returns and exchanges through an AI flow wired to your fulfilment and returns platform — zero tickets created.",
      },
      {
        title: "Review & UGC Collection",
        body: "Automated post-purchase review requests, photo/UGC capture and reputation routing that compounds social proof without manual chasing.",
      },
      {
        title: "Win-Back & Replenishment AI",
        body: "Predicts when a customer is due to reorder and triggers personalised replenishment offers — turning one-time buyers into subscribers.",
      },
    ],
    stack: [
      "Shopify",
      "Klaviyo",
      "Gorgias / Zendesk",
      "WhatsApp Business",
      "OpenAI / Claude",
      "Recharge",
      "Postscript SMS",
      "Meta / Google feeds",
      "Zapier / Make",
      "Custom API",
    ],
    caseStudy: {
      metric: "−68%",
      metricLabel: "of support tickets fully automated",
      badge: "Ecommerce • DTC • AI Support + Cart AI",
      title: "DTC brand cut support load by two-thirds and recovered 22% more carts.",
      body: "A growing DTC store was buried in WISMO ('where is my order') tickets and leaking cart revenue. We deployed an AI support agent wired to their fulfilment data plus an AI-personalised cart recovery flow. Within 6 weeks the AI was resolving 68% of tickets end-to-end and recovered cart revenue jumped 22% — with one fewer support hire needed.",
    },
    faq: [
      {
        q: "Will the AI agent give wrong answers to customers?",
        a: "We train it only on your verified policies, product data and FAQs, with strict guardrails and human-escalation for anything uncertain. It says 'let me get a teammate' rather than guessing.",
      },
      {
        q: "Does this replace my support team?",
        a: "No — it removes the repetitive 60–70% so your team focuses on complex, revenue-impacting conversations. Most clients redeploy staff rather than reduce headcount.",
      },
      {
        q: "How does it connect to Shopify and my tools?",
        a: "Through native integrations and APIs. The AI reads live order, inventory and shipping data so answers are always accurate and personalised.",
      },
    ],
  },

  /* ───────────────────────── FASHION & APPAREL ───────────────────────── */
  {
    aiSlug: "fashion-ai-automation",
    industrySlug: "fashion-apparel-marketing",
    metaTitle:
      "Fashion AI Automation — AI Stylist, Sizing & Support Agents | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for fashion & apparel brands: AI stylists, size-and-fit assistants, returns reduction and 24/7 support. Free automation audit.",
    heroEyebrow: "Fashion AI Automation",
    heroHeadline: "An AI Stylist and Fit Expert for Every Shopper",
    heroAccent: "Higher AOV. Fewer returns. Zero wait.",
    heroTagline:
      "We deploy AI that styles outfits, nails the size-and-fit question and supports shoppers around the clock — so apparel brands sell more per visit and send fewer items back.",
    heroStats: [
      { value: "−31%", label: "size-related returns" },
      { value: "+19%", label: "average order value" },
      { value: "24/7", label: "styling & support" },
    ],
    insightStats: [
      {
        value: "70%",
        label: "of fashion returns are size/fit",
        desc: "An AI fit assistant that asks the right questions cuts the costliest, most preventable returns before checkout.",
      },
      {
        value: "+19%",
        label: "AOV from AI styling",
        desc: "Conversational outfit-building turns a single-item purchase into a complete look.",
      },
      {
        value: "3.1×",
        label: "engagement vs. static size charts",
        desc: "Shoppers interact with a conversational fit guide far more than a table of measurements they ignore.",
      },
    ],
    automations: [
      {
        title: "AI Stylist & Outfit Builder",
        body: "A conversational stylist that learns the shopper's occasion, body type and taste, then builds complete outfits — lifting units per order.",
      },
      {
        title: "Size & Fit Assistant",
        body: "Asks a few smart questions (or matches a known brand size) and recommends the right size, cutting the returns that eat your margin.",
      },
      {
        title: "Returns-Reduction Automation",
        body: "Pre-purchase fit confidence plus an AI returns flow that offers exchanges and store credit before a refund is ever issued.",
      },
      {
        title: "24/7 Support & Order Agent",
        body: "Handles shipping, restock, order-status and care questions instantly across web, Instagram DM and WhatsApp.",
      },
      {
        title: "Restock & Drop Alerts",
        body: "AI captures interest on sold-out SKUs and upcoming drops, then triggers personalised back-in-stock and launch messages.",
      },
      {
        title: "Review & Lookbook UGC Engine",
        body: "Automated post-purchase requests for reviews and fit photos that fuel social proof and future styling recommendations.",
      },
    ],
    stack: [
      "Shopify",
      "Klaviyo",
      "Instagram / Meta DM",
      "WhatsApp Business",
      "OpenAI / Claude",
      "Size-recommendation API",
      "Gorgias",
      "Postscript SMS",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "−31%",
      metricLabel: "drop in size-related returns",
      badge: "Fashion • DTC Apparel • Fit AI + Stylist",
      title: "Apparel label cut fit returns by a third and grew AOV 19%.",
      body: "A DTC apparel label was losing margin to size returns and one-item orders. We launched an AI fit assistant on product pages and a conversational stylist for outfit-building. Size returns fell 31%, AOV rose 19% as shoppers added matching pieces, and the support inbox quieted down.",
    },
    faq: [
      {
        q: "How accurate is the AI size recommendation?",
        a: "It improves continuously from your real return and exchange data, plus brand-to-brand size mapping. Accuracy climbs quickly as it learns your specific fits.",
      },
      {
        q: "Can the stylist match my brand's aesthetic?",
        a: "Yes — it's trained on your catalogue, collections and styling guidelines so recommendations always feel on-brand.",
      },
      {
        q: "Does it work in Instagram DMs?",
        a: "Yes. A huge share of fashion discovery happens on Instagram, so the agent answers and styles directly in DMs as well as on-site.",
      },
    ],
  },

  /* ───────────────────────── REAL ESTATE ───────────────────────── */
  {
    aiSlug: "real-estate-ai-automation",
    industrySlug: "real-estate-marketing",
    metaTitle:
      "Real Estate AI Automation — Instant Lead Response & Listing AI | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for real estate: instant lead-response agents, 24/7 property-matching chatbots, viewing scheduling and nurture. Never miss a lead again. Free audit.",
    heroEyebrow: "Real Estate AI Automation",
    heroHeadline: "Respond to Every Lead in Seconds — Day or Night",
    heroAccent: "More viewings booked. Zero leads lost.",
    heroTagline:
      "We deploy AI that instantly answers and qualifies every enquiry, matches buyers to listings, books viewings into your calendar and nurtures cold leads — so agents only ever talk to ready-to-act prospects.",
    heroStats: [
      { value: "30 sec", label: "average lead-response time" },
      { value: "+47%", label: "lead-to-viewing conversion" },
      { value: "24/7", label: "enquiry coverage" },
    ],
    insightStats: [
      {
        value: "78%",
        label: "of deals go to the first responder",
        desc: "Buyers and sellers contact several agents. The one who replies first usually wins — and humans can't beat a 30-second AI response at 11pm.",
      },
      {
        value: "5 min",
        label: "is the make-or-break response window",
        desc: "Lead conversion drops up to 10× after the first 5 minutes. AI guarantees an instant, qualified reply every single time.",
      },
      {
        value: "48%",
        label: "of enquiries arrive after hours",
        desc: "Nearly half your leads come in evenings and weekends. AI captures, qualifies and books them while you sleep.",
      },
    ],
    automations: [
      {
        title: "Instant Lead-Response AI",
        body: "The moment a portal, ad or website enquiry lands, AI replies in seconds via SMS, WhatsApp or email — qualifying budget, timeline and intent before a competitor even sees it.",
      },
      {
        title: "Property-Matching Chatbot",
        body: "A 24/7 assistant on your site that understands what a buyer wants and surfaces matching listings, capturing contact details and booking viewings.",
      },
      {
        title: "Viewing & Valuation Scheduling",
        body: "AI offers real-time availability and books viewings or valuations straight into your (and your agents') calendars — with automated reminders to cut no-shows.",
      },
      {
        title: "Seller / Vendor Lead Qualification",
        body: "Captures and qualifies listing leads, asks the right questions about the property and timeline, and routes hot vendors to the right agent instantly.",
      },
      {
        title: "Long-Term Nurture AI",
        body: "Most buyers aren't ready today. AI keeps cold leads warm with personalised market updates and check-ins, re-engaging them the moment they're ready to act.",
      },
      {
        title: "Review & Referral Automation",
        body: "After a completed deal, AI requests reviews and referrals at the perfect moment — compounding your reputation and pipeline.",
      },
    ],
    stack: [
      "Rightmove / Zillow / portals",
      "HubSpot / Follow Up Boss",
      "WhatsApp Business",
      "Calendly / calendar sync",
      "OpenAI / Claude",
      "Twilio SMS",
      "Salesforce",
      "Zapier / Make",
      "Custom CRM API",
    ],
    caseStudy: {
      metric: "+47%",
      metricLabel: "more viewings booked from the same leads",
      badge: "Real Estate • Agency • Lead-Response AI",
      title: "Agency booked 47% more viewings without spending a dollar more on ads.",
      body: "A multi-agent brokerage was losing portal leads to faster competitors and missing every after-hours enquiry. We deployed instant lead-response AI across SMS and WhatsApp plus a property-matching chatbot with calendar booking. Response time dropped to ~30 seconds, after-hours leads were finally captured, and viewings booked from the same lead volume rose 47%.",
    },
    faq: [
      {
        q: "Will leads know they're talking to an AI?",
        a: "The AI is transparent and natural, handling qualification and booking, then handing warm, ready prospects to your agents with full context. Clients consistently rate the experience highly.",
      },
      {
        q: "Does it connect to my CRM and the property portals?",
        a: "Yes — it integrates with Follow Up Boss, HubSpot, Salesforce and portal lead feeds (Rightmove, Zillow, etc.), so every lead is captured and logged automatically.",
      },
      {
        q: "What happens to leads that aren't ready to buy yet?",
        a: "They enter an AI nurture track with relevant market updates and periodic check-ins, so they re-engage with you — not a competitor — when the time comes.",
      },
    ],
  },

  /* ───────────────────────── MORTGAGE & FINANCE BROKERS ───────────────────────── */
  {
    aiSlug: "mortgage-broker-ai-automation",
    industrySlug: "mortgage-broker-marketing",
    metaTitle:
      "Mortgage Broker AI Automation — Lead Qualification & Doc Collection | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for mortgage & finance brokers: instant lead qualification, document collection, appointment booking and compliant nurture. Free automation audit.",
    heroEyebrow: "Mortgage & Finance AI Automation",
    heroHeadline: "Qualify Borrowers and Collect Docs — Automatically",
    heroAccent: "Faster approvals. Fuller pipeline. Less admin.",
    heroTagline:
      "We deploy AI that instantly qualifies enquiries, books appointments, chases the documents you need and nurtures rate-shoppers — so brokers spend time advising, not chasing paperwork.",
    heroStats: [
      { value: "30 sec", label: "lead-response time" },
      { value: "−60%", label: "admin hours per file" },
      { value: "24/7", label: "enquiry handling" },
    ],
    insightStats: [
      {
        value: "78%",
        label: "of borrowers pick the first broker to respond",
        desc: "Rate-shoppers move fast. Instant AI qualification means you're first in the conversation, every time.",
      },
      {
        value: "11 days",
        label: "average delay from missing documents",
        desc: "Approvals stall waiting on payslips and statements. AI chases and collects them automatically — compressing your timeline.",
      },
      {
        value: "60%",
        label: "of broker time lost to admin",
        desc: "Qualification, follow-up and doc collection eat the day. Automating them frees brokers to write more business.",
      },
    ],
    automations: [
      {
        title: "Instant Lead Qualification AI",
        body: "Replies in seconds to capture loan type, amount, employment and timeline, scoring each lead so you prioritise the ones ready to proceed.",
      },
      {
        title: "Automated Document Collection",
        body: "AI requests, reminds and securely collects payslips, IDs and statements via a guided flow — no more chasing clients by phone.",
      },
      {
        title: "Appointment Booking",
        body: "Offers live availability and books discovery and application calls into your calendar, with reminders that cut no-shows.",
      },
      {
        title: "Rate-Shopper Nurture",
        body: "Keeps not-yet-ready borrowers engaged with rate updates and helpful content, re-engaging them the moment they're ready.",
      },
      {
        title: "Renewal & Refinance Triggers",
        body: "Tracks fixed-term expiries and rate movements, then proactively reaches out to past clients about refinancing — reactivating dormant revenue.",
      },
      {
        title: "Review & Referral Automation",
        body: "Requests reviews and partner/agent referrals at settlement, building reputation and a self-sustaining pipeline.",
      },
    ],
    stack: [
      "HubSpot / Salesforce",
      "Secure doc upload",
      "Calendly / calendar sync",
      "WhatsApp / SMS",
      "OpenAI / Claude",
      "DocuSign",
      "CRM / aggregator API",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "−60%",
      metricLabel: "reduction in admin time per file",
      badge: "Mortgage • Brokerage • Qualification + Doc AI",
      title: "Brokerage halved admin and lifted submission speed with AI.",
      body: "A growing brokerage had advisers buried in qualification calls and document chasing. We deployed instant AI qualification plus automated, secure document collection with reminders. Admin time per file fell ~60%, files reached submission faster, and advisers reclaimed hours to write more loans.",
    },
    faq: [
      {
        q: "Is client document handling secure and compliant?",
        a: "Yes — documents are collected through encrypted, access-controlled flows, and the AI follows scripted, compliant qualification. We design around your regulatory requirements.",
      },
      {
        q: "Does the AI give financial advice?",
        a: "No. It qualifies, books, collects and nurtures. All regulated advice stays with your licensed brokers — the AI simply removes the admin around it.",
      },
      {
        q: "Will it work with my CRM and aggregator?",
        a: "Yes — we integrate with HubSpot, Salesforce and common aggregator/CRM systems so every lead and document flows to the right place.",
      },
    ],
  },

  /* ───────────────────────── HEALTHCARE ───────────────────────── */
  {
    aiSlug: "healthcare-ai-automation",
    industrySlug: "healthcare-marketing",
    metaTitle:
      "Healthcare AI Automation — Patient Intake, Scheduling & Reminders | Lumotis",
    metaDescription:
      "Lumotis builds HIPAA-aware AI automation for healthcare: patient intake, appointment scheduling, no-show reduction, recall and 24/7 enquiry handling. Free automation audit.",
    heroEyebrow: "Healthcare AI Automation",
    heroHeadline: "Fill the Schedule, Cut No-Shows, Free Your Front Desk",
    heroAccent: "More patients seen. Less phone tag.",
    heroTagline:
      "We deploy privacy-conscious AI that handles patient enquiries, books and reschedules appointments, reduces no-shows and runs recall campaigns — so your front desk stops drowning in calls.",
    heroStats: [
      { value: "−38%", label: "patient no-shows" },
      { value: "24/7", label: "appointment booking" },
      { value: "−50%", label: "front-desk call volume" },
    ],
    insightStats: [
      {
        value: "30%",
        label: "of calls go unanswered at peak",
        desc: "Every missed call is a lost patient. AI answers and books 24/7, so no enquiry slips away.",
      },
      {
        value: "$200+",
        label: "lost revenue per no-show",
        desc: "Smart AI reminders and easy rescheduling recover slots that would otherwise sit empty.",
      },
      {
        value: "67%",
        label: "of patients prefer to self-book online",
        desc: "Patients want to book without a phone call. AI scheduling meets them where they are — day or night.",
      },
    ],
    automations: [
      {
        title: "AI Patient Intake & Triage",
        body: "Collects symptoms, history and insurance details through a guided conversation and routes patients to the right service or provider.",
      },
      {
        title: "24/7 Scheduling & Rescheduling",
        body: "Patients book, confirm and reschedule appointments any time, with real-time calendar sync to your practice management system.",
      },
      {
        title: "No-Show Reduction",
        body: "Multi-touch AI reminders via SMS and email, with one-tap confirm/reschedule that fills cancelled slots automatically from a waitlist.",
      },
      {
        title: "Recall & Recare Campaigns",
        body: "Automatically reaches out to patients due for check-ups, screenings or follow-ups — reactivating dormant patients into booked appointments.",
      },
      {
        title: "After-Hours Enquiry Agent",
        body: "Answers common questions about services, hours, locations and preparation instructions when the front desk is closed.",
      },
      {
        title: "Review & Reputation Automation",
        body: "Requests reviews from satisfied patients at the right moment and routes any concerns privately — protecting and growing your reputation.",
      },
    ],
    stack: [
      "Practice management / EHR",
      "Calendar sync",
      "Secure SMS / email",
      "OpenAI / Claude",
      "Twilio",
      "Patient portal API",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "−38%",
      metricLabel: "reduction in patient no-shows",
      badge: "Healthcare • Multi-location clinic • Scheduling AI",
      title: "Clinic group cut no-shows 38% and freed the front desk.",
      body: "A multi-location clinic group had overwhelmed phone lines and a costly no-show rate. We deployed AI scheduling with multi-touch reminders, waitlist auto-fill and a recall engine. No-shows dropped 38%, front-desk call volume halved, and previously dormant patients returned through automated recare outreach.",
    },
    faq: [
      {
        q: "Is this compliant with patient-privacy regulations?",
        a: "We build with privacy and security first — encrypted data handling and access controls designed around HIPAA/GDPR-style requirements. We scope compliance to your jurisdiction before launch.",
      },
      {
        q: "Does it integrate with our EHR/practice software?",
        a: "Yes — we connect to common practice-management and EHR systems via API or supported integrations so scheduling and records stay in sync.",
      },
      {
        q: "Can the AI handle clinical questions?",
        a: "It handles administrative and triage routing, not clinical advice. Anything clinical is escalated to your staff with full context.",
      },
    ],
  },

  /* ───────────────────────── DENTAL ───────────────────────── */
  {
    aiSlug: "dental-ai-automation",
    industrySlug: "dental-marketing",
    metaTitle:
      "Dental AI Automation — Booking, No-Show Reduction & Recall | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for dental & orthodontic practices: 24/7 booking, no-show reduction, recall reactivation, treatment follow-up and review automation. Free audit.",
    heroEyebrow: "Dental AI Automation",
    heroHeadline: "A Full Chair, Fewer No-Shows, a Quieter Front Desk",
    heroAccent: "More booked treatments. Less phone tag.",
    heroTagline:
      "We deploy AI that books and reschedules patients 24/7, slashes no-shows, reactivates lapsed patients and follows up on treatment plans — so your schedule stays full without the front-desk grind.",
    heroStats: [
      { value: "−40%", label: "no-show rate" },
      { value: "24/7", label: "online booking" },
      { value: "+28%", label: "lapsed-patient reactivation" },
    ],
    insightStats: [
      {
        value: "$250+",
        label: "lost per missed hygiene appointment",
        desc: "Empty chairs are pure lost revenue. AI reminders and waitlist auto-fill keep the schedule tight.",
      },
      {
        value: "45%",
        label: "of patients overdue for recall",
        desc: "A huge book of dormant patients is sitting in your software. AI recall outreach reactivates them automatically.",
      },
      {
        value: "60%",
        label: "of booking requests are after hours",
        desc: "Patients want to book in the evening. AI captures and schedules them when your front desk is gone.",
      },
    ],
    automations: [
      {
        title: "24/7 AI Booking Agent",
        body: "Patients book, confirm and reschedule any time across web, SMS and WhatsApp, synced live to your practice calendar.",
      },
      {
        title: "No-Show & Reminder Automation",
        body: "Smart multi-touch reminders with one-tap confirm, plus automatic waitlist fill when someone cancels — keeping chairs full.",
      },
      {
        title: "Recall & Reactivation AI",
        body: "Identifies patients overdue for hygiene or check-ups and runs personalised win-back outreach that books them straight in.",
      },
      {
        title: "Treatment-Plan Follow-Up",
        body: "Nurtures patients who haven't yet scheduled accepted treatment (implants, ortho, whitening) until they book.",
      },
      {
        title: "New-Patient Enquiry Agent",
        body: "Answers questions on services, insurance, pricing and availability instantly, capturing and converting new-patient leads.",
      },
      {
        title: "Review & Referral Automation",
        body: "Requests reviews from happy patients at the ideal moment and routes any issues privately to protect your reputation.",
      },
    ],
    stack: [
      "Dentrix / Open Dental / practice software",
      "Calendar sync",
      "SMS / WhatsApp",
      "OpenAI / Claude",
      "Twilio",
      "Review platforms",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "−40%",
      metricLabel: "drop in no-show rate",
      badge: "Dental • Group practice • Booking + Recall AI",
      title: "Dental group cut no-shows 40% and reactivated dormant patients.",
      body: "A dental group had a high no-show rate and thousands of lapsed patients in their software. We deployed AI booking with multi-touch reminders and waitlist auto-fill, plus an automated recall engine. No-shows fell 40%, the front desk reclaimed hours, and lapsed-patient reactivation rose 28% — filling chairs from patients they already had.",
    },
    faq: [
      {
        q: "Will it work with my practice-management software?",
        a: "Yes — we integrate with systems like Dentrix and Open Dental (and others) so bookings and reminders stay in sync with your calendar.",
      },
      {
        q: "How does it reduce no-shows specifically?",
        a: "Multi-touch reminders with one-tap confirm/reschedule, plus automatic waitlist fill so cancelled slots get rebooked instead of sitting empty.",
      },
      {
        q: "Can it handle insurance questions?",
        a: "It answers common coverage and pricing questions from your verified information and escalates specifics to your team.",
      },
    ],
  },

  /* ───────────────────────── CHIROPRACTIC & PHYSIO ───────────────────────── */
  {
    aiSlug: "chiropractic-ai-automation",
    industrySlug: "chiropractic-marketing",
    metaTitle:
      "Chiropractic & Physio AI Automation — Booking, Reactivation & Reminders | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for chiropractic & physiotherapy clinics: 24/7 booking, no-show reduction, plan-of-care follow-up and patient reactivation. Free audit.",
    heroEyebrow: "Chiropractic & Physio AI Automation",
    heroHeadline: "Keep Patients on Plan and Chairs Full — Automatically",
    heroAccent: "Better retention. Fewer drop-offs.",
    heroTagline:
      "We deploy AI that books appointments, reduces no-shows, follows up on care plans and reactivates patients who fell off — so your retention and revenue stop leaking.",
    heroStats: [
      { value: "−37%", label: "no-shows & drop-offs" },
      { value: "24/7", label: "online booking" },
      { value: "+25%", label: "care-plan adherence" },
    ],
    insightStats: [
      {
        value: "50%",
        label: "of patients drop off before plan completion",
        desc: "Care plans only work if patients finish them. AI follow-up keeps them on track and on the schedule.",
      },
      {
        value: "$120+",
        label: "lost per missed visit",
        desc: "Frequent visits mean frequent gaps. AI reminders and rebooking protect the revenue between sessions.",
      },
      {
        value: "40%",
        label: "of past patients are reactivatable",
        desc: "Old patients re-injure and relapse. AI check-ins bring them back before they go elsewhere.",
      },
    ],
    automations: [
      {
        title: "24/7 Booking & Rebooking",
        body: "Patients schedule and reschedule any time, with the AI proactively rebooking the next visit in a care plan before they leave.",
      },
      {
        title: "Plan-of-Care Follow-Up",
        body: "Automated check-ins keep patients adherent to their treatment schedule, reducing mid-plan drop-off.",
      },
      {
        title: "No-Show Reduction",
        body: "Multi-touch reminders with easy confirm/reschedule and waitlist auto-fill keep the appointment book tight.",
      },
      {
        title: "Patient Reactivation AI",
        body: "Reaches out to lapsed patients with relevant check-ins and offers, recovering revenue from people you already treated.",
      },
      {
        title: "New-Patient Enquiry Agent",
        body: "Answers questions on conditions treated, pricing and insurance instantly, converting more enquiries into first visits.",
      },
      {
        title: "Review & Referral Automation",
        body: "Collects reviews from satisfied patients and encourages referrals at the right moment in their care journey.",
      },
    ],
    stack: [
      "Cliniko / Jane / practice software",
      "Calendar sync",
      "SMS / WhatsApp",
      "OpenAI / Claude",
      "Twilio",
      "Review platforms",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "+25%",
      metricLabel: "improvement in care-plan adherence",
      badge: "Chiro & Physio • Clinic • Retention AI",
      title: "Clinic lifted plan adherence 25% and reactivated lapsed patients.",
      body: "A busy chiro/physio clinic was losing patients mid-plan and leaving past patients dormant. We deployed AI rebooking, plan-of-care follow-up and a reactivation engine. Care-plan adherence rose 25%, no-shows and drop-offs fell 37%, and a steady stream of lapsed patients returned automatically.",
    },
    faq: [
      {
        q: "Does it integrate with Cliniko or Jane?",
        a: "Yes — we connect to common clinic-management platforms so scheduling, reminders and reactivation stay in sync.",
      },
      {
        q: "How does it improve retention?",
        a: "By proactively rebooking the next visit and following up on the care plan so patients don't quietly drop off mid-treatment.",
      },
      {
        q: "Is patient data handled securely?",
        a: "Yes — encrypted, access-controlled flows designed around healthcare privacy requirements in your jurisdiction.",
      },
    ],
  },

  /* ───────────────────────── COSMETIC SURGERY & AESTHETICS ───────────────────────── */
  {
    aiSlug: "cosmetic-surgery-ai-automation",
    industrySlug: "cosmetic-surgery-marketing",
    metaTitle:
      "Cosmetic & Aesthetics AI Automation — Consult Booking & Lead Nurture | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for cosmetic surgery & aesthetics clinics: instant lead response, consultation booking, high-value nurture and review automation. Free audit.",
    heroEyebrow: "Cosmetic & Aesthetics AI Automation",
    heroHeadline: "Turn High-Value Enquiries into Booked Consultations",
    heroAccent: "More consults. Higher conversion. Discreet.",
    heroTagline:
      "We deploy AI that responds to high-intent enquiries instantly, qualifies discreetly, books consultations and nurtures considered, high-ticket decisions — so no premium lead slips away.",
    heroStats: [
      { value: "30 sec", label: "lead-response time" },
      { value: "+44%", label: "consult booking rate" },
      { value: "24/7", label: "enquiry handling" },
    ],
    insightStats: [
      {
        value: "$5k–$30k",
        label: "typical procedure value",
        desc: "With this much at stake per lead, a single missed enquiry is expensive. Instant AI response protects every one.",
      },
      {
        value: "6–12 wks",
        label: "average decision window",
        desc: "Aesthetic decisions take time. AI nurture keeps your clinic top-of-mind through the whole consideration period.",
      },
      {
        value: "70%",
        label: "of enquiries want discretion first",
        desc: "Patients value privacy. A discreet, judgement-free AI conversation increases the odds they book a consult.",
      },
    ],
    automations: [
      {
        title: "Instant High-Value Lead Response",
        body: "Replies in seconds to enquiries from ads, site and social, qualifying procedure interest and budget before a competitor responds.",
      },
      {
        title: "Consultation Booking",
        body: "Books in-person and virtual consultations into the right surgeon's or practitioner's calendar, with deposit and reminder flows.",
      },
      {
        title: "High-Ticket Nurture AI",
        body: "Sends tailored education, before/after proof and financing options across the long decision window until the patient is ready.",
      },
      {
        title: "Discreet Enquiry Agent",
        body: "A private, judgement-free 24/7 assistant that answers sensitive questions about procedures, recovery and pricing.",
      },
      {
        title: "Pre & Post-Procedure Automation",
        body: "Delivers prep instructions, aftercare check-ins and follow-up booking automatically — improving outcomes and reviews.",
      },
      {
        title: "Review & Referral Engine",
        body: "Requests reviews and referrals at the ideal post-procedure moment, building the social proof that drives premium enquiries.",
      },
    ],
    stack: [
      "CRM (HubSpot/Salesforce)",
      "Calendar sync",
      "WhatsApp / SMS",
      "OpenAI / Claude",
      "Deposit / payment links",
      "Review platforms",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "+44%",
      metricLabel: "increase in booked consultations",
      badge: "Aesthetics • Clinic • Lead-Response + Nurture AI",
      title: "Aesthetics clinic booked 44% more consults from the same ad spend.",
      body: "A cosmetic clinic was paying premium ad costs but losing high-value leads to slow replies and weak follow-up. We deployed instant lead-response AI plus a long-window nurture track with education and financing. Consultation bookings rose 44%, after-hours enquiries were finally captured, and high-ticket leads stayed engaged through the full decision window.",
    },
    faq: [
      {
        q: "Is the AI discreet with sensitive enquiries?",
        a: "Yes — it's designed for private, judgement-free conversations and follows your tone and compliance guidelines for medical/aesthetic communication.",
      },
      {
        q: "Can it handle financing and deposit questions?",
        a: "It shares your financing options and can send deposit/payment links to secure consultations, escalating specifics to your team.",
      },
      {
        q: "Does it nurture leads that don't book immediately?",
        a: "Yes — that's where it shines. It keeps high-ticket leads engaged with proof and education across the long decision window.",
      },
    ],
  },

  /* ───────────────────────── MENTAL HEALTH & THERAPY ───────────────────────── */
  {
    aiSlug: "mental-health-ai-automation",
    industrySlug: "mental-health-marketing",
    metaTitle:
      "Mental Health AI Automation — Intake, Matching & Scheduling | Lumotis",
    metaDescription:
      "Lumotis builds compassionate, privacy-first AI automation for therapy & mental health practices: client intake, therapist matching, scheduling and reminders. Free audit.",
    heroEyebrow: "Mental Health AI Automation",
    heroHeadline: "Match Clients to the Right Therapist — Faster and Kinder",
    heroAccent: "Less wait. Less admin. More care.",
    heroTagline:
      "We deploy compassionate, privacy-first AI that handles intake, matches clients to the right therapist, books sessions and sends gentle reminders — so people get help sooner and your team focuses on care.",
    heroStats: [
      { value: "−45%", label: "intake admin time" },
      { value: "24/7", label: "enquiry & booking" },
      { value: "−33%", label: "session no-shows" },
    ],
    insightStats: [
      {
        value: "11 days",
        label: "average wait to first session",
        desc: "Long intake delays cause drop-off when people are most vulnerable. AI shortens the path from enquiry to care.",
      },
      {
        value: "50%",
        label: "of enquiries arrive after hours",
        desc: "People reach out when they're struggling — often at night. AI responds with warmth, any time.",
      },
      {
        value: "20%",
        label: "typical first-session no-show rate",
        desc: "Gentle, supportive reminders reduce no-shows without feeling clinical or pushy.",
      },
    ],
    automations: [
      {
        title: "Compassionate Intake AI",
        body: "A warm, private conversation that gathers needs, preferences and history, easing clients into care without an intimidating form.",
      },
      {
        title: "Therapist Matching",
        body: "Matches clients to the right therapist by specialty, modality, availability and preference — reducing mismatches and early drop-off.",
      },
      {
        title: "Scheduling & Rescheduling",
        body: "Books and reschedules sessions 24/7 with calendar sync, so clients secure care the moment they decide to.",
      },
      {
        title: "Gentle Reminder Automation",
        body: "Supportive, non-clinical reminders with easy reschedule that reduce no-shows while respecting the sensitivity of care.",
      },
      {
        title: "Waitlist & Re-engagement",
        body: "Manages waitlists and gently re-engages clients who paused, helping them return to care when they're ready.",
      },
      {
        title: "Feedback & Reputation Automation",
        body: "Requests feedback and reviews thoughtfully, routing any concerns privately and with care.",
      },
    ],
    stack: [
      "SimplePractice / practice software",
      "Calendar sync",
      "Secure SMS / email",
      "OpenAI / Claude",
      "Telehealth platforms",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "−45%",
      metricLabel: "reduction in intake admin time",
      badge: "Mental Health • Group practice • Intake + Matching AI",
      title: "Therapy group shortened intake and reduced first-session no-shows.",
      body: "A therapy group practice had slow intake and a high first-session no-show rate. We deployed a compassionate AI intake and matching flow with gentle reminders. Intake admin fell 45%, clients reached the right therapist faster, and first-session no-shows dropped 33% — getting people into care sooner.",
    },
    faq: [
      {
        q: "Is the AI appropriate for sensitive mental-health enquiries?",
        a: "It's designed to be warm, non-judgemental and clearly administrative — handling intake, matching and scheduling, never therapy. Crisis situations are routed to appropriate resources immediately.",
      },
      {
        q: "Is client data kept private and compliant?",
        a: "Yes — encrypted, access-controlled handling designed around healthcare-privacy requirements in your region.",
      },
      {
        q: "Does it integrate with our practice software?",
        a: "Yes — we connect to platforms like SimplePractice and others so scheduling and records stay in sync.",
      },
    ],
  },

  /* ───────────────────────── SALON & SPA ───────────────────────── */
  {
    aiSlug: "salon-spa-ai-automation",
    industrySlug: "salon-spa-marketing",
    metaTitle:
      "Salon & Spa AI Automation — Booking, Rebooking & Reminders | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for salons & spas: 24/7 booking, no-show reduction, automatic rebooking, win-back campaigns and review automation. Free audit.",
    heroEyebrow: "Salon & Spa AI Automation",
    heroHeadline: "A Fully Booked Calendar Without the Phone Ringing",
    heroAccent: "More rebookings. Fewer empty chairs.",
    heroTagline:
      "We deploy AI that books and rebooks clients 24/7, cuts no-shows, runs win-back campaigns and collects reviews — so your chairs stay full and your front desk stays calm.",
    heroStats: [
      { value: "−42%", label: "no-show rate" },
      { value: "24/7", label: "online booking" },
      { value: "+30%", label: "rebooking rate" },
    ],
    insightStats: [
      {
        value: "40%",
        label: "of bookings happen after hours",
        desc: "Clients book at night and on weekends. AI captures and schedules them when you're closed.",
      },
      {
        value: "$80+",
        label: "lost per empty chair hour",
        desc: "Gaps and no-shows are pure lost revenue. AI reminders and waitlist fill keep the calendar tight.",
      },
      {
        value: "3×",
        label: "more valuable to rebook than acquire",
        desc: "Rebooking existing clients is far cheaper than finding new ones. AI nudges every client to book their next visit.",
      },
    ],
    automations: [
      {
        title: "24/7 Booking Agent",
        body: "Clients book any service with any stylist or therapist via web, Instagram DM and SMS, synced live to your calendar.",
      },
      {
        title: "Automatic Rebooking",
        body: "Prompts every client to book their next appointment at the perfect interval, lifting retention and lifetime value.",
      },
      {
        title: "No-Show & Reminder Automation",
        body: "Smart reminders with one-tap confirm and waitlist auto-fill keep gaps from costing you revenue.",
      },
      {
        title: "Win-Back Campaigns",
        body: "Detects clients who haven't returned and sends personalised offers to bring them back before they find a new salon.",
      },
      {
        title: "Upsell & Package AI",
        body: "Recommends add-ons, packages and memberships during booking, raising average spend per visit.",
      },
      {
        title: "Review & Referral Engine",
        body: "Requests reviews from happy clients and encourages referrals automatically — fueling new bookings.",
      },
    ],
    stack: [
      "Fresha / Vagaro / Mindbody",
      "Calendar sync",
      "Instagram DM / SMS",
      "OpenAI / Claude",
      "Review platforms",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "+30%",
      metricLabel: "increase in rebooking rate",
      badge: "Salon & Spa • Multi-chair • Booking + Rebook AI",
      title: "Salon lifted rebookings 30% and cut no-shows 42%.",
      body: "A busy salon was losing revenue to gaps, no-shows and clients who never rebooked. We deployed 24/7 AI booking with automatic rebooking, reminders and win-back campaigns. Rebooking rose 30%, no-shows fell 42%, and lapsed clients returned through automated offers — all while the front desk fielded far fewer calls.",
    },
    faq: [
      {
        q: "Does it work with Fresha, Vagaro or Mindbody?",
        a: "Yes — we integrate with the major salon/spa booking platforms so availability and bookings stay perfectly in sync.",
      },
      {
        q: "Can clients book through Instagram?",
        a: "Yes — a lot of salon discovery happens on Instagram, so the AI books directly in DMs as well as on your site.",
      },
      {
        q: "How does automatic rebooking work?",
        a: "After each visit the AI nudges clients to book their next appointment at the ideal interval for their service — the single biggest lever on retention.",
      },
    ],
  },

  /* ───────────────────────── FITNESS & GYM ───────────────────────── */
  {
    aiSlug: "fitness-ai-automation",
    industrySlug: "fitness-gym-marketing",
    metaTitle:
      "Fitness & Gym AI Automation — Lead Response, Trials & Retention | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for gyms & fitness studios: instant lead response, trial booking, onboarding, churn-prevention and win-back. Free automation audit.",
    heroEyebrow: "Fitness & Gym AI Automation",
    heroHeadline: "Convert More Leads and Keep Members Longer — Automatically",
    heroAccent: "More trials booked. Lower churn.",
    heroTagline:
      "We deploy AI that responds to enquiries instantly, books trials and tours, onboards new members and spots churn risk early — so you fill classes and keep members paying.",
    heroStats: [
      { value: "30 sec", label: "lead-response time" },
      { value: "+38%", label: "trial-to-member conversion" },
      { value: "−24%", label: "member churn" },
    ],
    insightStats: [
      {
        value: "78%",
        label: "of leads pick the first gym to respond",
        desc: "Fitness buyers are impulsive. Instant AI response books the trial before motivation fades.",
      },
      {
        value: "First 30 days",
        label: "decide whether members stay",
        desc: "Early engagement predicts retention. AI onboarding keeps new members active and committed.",
      },
      {
        value: "5×",
        label: "cheaper to retain than acquire",
        desc: "Churn is the silent killer of gym revenue. AI catches at-risk members before they cancel.",
      },
    ],
    automations: [
      {
        title: "Instant Lead-Response AI",
        body: "Replies in seconds to ad and web enquiries, qualifies goals and books a trial or tour before the lead cools off.",
      },
      {
        title: "Trial & Tour Booking",
        body: "Schedules trials, tours and intro sessions into your calendar with reminders that maximise show-up rate.",
      },
      {
        title: "Member Onboarding AI",
        body: "Guides new members through their first 30 days with check-ins and class recommendations that build the habit.",
      },
      {
        title: "Churn-Prevention Triggers",
        body: "Detects drops in attendance and proactively re-engages at-risk members before they cancel.",
      },
      {
        title: "Win-Back Campaigns",
        body: "Reaches lapsed and cancelled members with personalised comeback offers at the right moment.",
      },
      {
        title: "Review & Referral Engine",
        body: "Requests reviews from happy members and runs referral prompts that turn your community into a growth channel.",
      },
    ],
    stack: [
      "Mindbody / Glofox / gym CRM",
      "Calendar sync",
      "SMS / WhatsApp",
      "OpenAI / Claude",
      "Access-control data",
      "Review platforms",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "−24%",
      metricLabel: "reduction in member churn",
      badge: "Fitness • Studio group • Lead + Retention AI",
      title: "Studio group converted more trials and cut churn 24%.",
      body: "A fitness studio group was slow to respond to leads and losing members quietly. We deployed instant lead-response AI with trial booking, plus onboarding and churn-prevention automations tied to attendance data. Trial-to-member conversion rose 38% and churn dropped 24% as at-risk members were re-engaged automatically.",
    },
    faq: [
      {
        q: "Does it integrate with Mindbody or Glofox?",
        a: "Yes — we connect to the major gym/studio platforms for booking, attendance and membership data.",
      },
      {
        q: "How does it predict churn?",
        a: "By watching attendance patterns. When a member's visits drop, the AI re-engages them with relevant nudges before they cancel.",
      },
      {
        q: "Can it book trials from Instagram and ads?",
        a: "Yes — it responds instantly across web, ads, SMS and DMs, booking trials before the lead loses momentum.",
      },
    ],
  },

  /* ───────────────────────── VETERINARY & PET ───────────────────────── */
  {
    aiSlug: "veterinary-ai-automation",
    industrySlug: "veterinary-marketing",
    metaTitle:
      "Veterinary AI Automation — Booking, Reminders & Recall | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for veterinary clinics & pet services: 24/7 booking, vaccination & check-up recall, no-show reduction and after-hours triage routing. Free audit.",
    heroEyebrow: "Veterinary AI Automation",
    heroHeadline: "Keep Every Pet on Schedule — Without the Phone Chaos",
    heroAccent: "More appointments. Fewer missed recalls.",
    heroTagline:
      "We deploy AI that books appointments 24/7, runs vaccination and check-up recall, reduces no-shows and routes after-hours enquiries — so pets stay healthy and your front desk stays sane.",
    heroStats: [
      { value: "−36%", label: "no-show rate" },
      { value: "24/7", label: "booking & enquiries" },
      { value: "+29%", label: "recall compliance" },
    ],
    insightStats: [
      {
        value: "45%",
        label: "of pets overdue for preventive care",
        desc: "Missed vaccinations and check-ups are everywhere. AI recall outreach books them automatically.",
      },
      {
        value: "30%",
        label: "of calls go unanswered at peak",
        desc: "Front desks can't keep up. AI answers and books around the clock so no pet owner is left waiting.",
      },
      {
        value: "$150+",
        label: "lost per missed appointment",
        desc: "Reminders and waitlist fill protect revenue from empty slots.",
      },
    ],
    automations: [
      {
        title: "24/7 AI Booking Agent",
        body: "Pet owners book and reschedule appointments any time across web, SMS and WhatsApp, synced to your practice calendar.",
      },
      {
        title: "Vaccination & Check-up Recall",
        body: "Automatically reaches owners when pets are due for vaccinations, dentals or wellness visits — and books them in.",
      },
      {
        title: "No-Show Reduction",
        body: "Multi-touch reminders with one-tap confirm and waitlist auto-fill keep the schedule full.",
      },
      {
        title: "After-Hours Enquiry Routing",
        body: "Answers common questions and triages urgency, directing emergencies to the right resource immediately.",
      },
      {
        title: "Preventive-Care & Plan Nurture",
        body: "Promotes wellness plans and preventive care with timely, personalised outreach that improves pet health and clinic revenue.",
      },
      {
        title: "Review & Referral Automation",
        body: "Requests reviews from happy pet owners and encourages referrals at the right moment.",
      },
    ],
    stack: [
      "ezyVet / Cornerstone / PIMS",
      "Calendar sync",
      "SMS / WhatsApp",
      "OpenAI / Claude",
      "Review platforms",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "+29%",
      metricLabel: "improvement in recall compliance",
      badge: "Veterinary • Clinic • Booking + Recall AI",
      title: "Vet clinic improved recall compliance 29% and cut no-shows.",
      body: "A veterinary clinic had overwhelmed phone lines and pets falling behind on preventive care. We deployed AI booking plus an automated vaccination/check-up recall engine with reminders. Recall compliance rose 29%, no-shows fell 36%, and the front desk reclaimed hours previously lost to the phone.",
    },
    faq: [
      {
        q: "Does it integrate with our practice information system (PIMS)?",
        a: "Yes — we connect to systems like ezyVet and Cornerstone (and others) to keep booking and recall in sync with your records.",
      },
      {
        q: "Can it triage emergencies safely?",
        a: "It identifies urgent situations and immediately routes owners to the right emergency resource or staff member — it never delays urgent care.",
      },
      {
        q: "How does the recall engine work?",
        a: "It tracks due dates for vaccinations and check-ups and proactively reaches out to book pets in before they fall behind.",
      },
    ],
  },

  /* ───────────────────────── RESTAURANTS & FOOD ───────────────────────── */
  {
    aiSlug: "restaurant-ai-automation",
    industrySlug: "restaurant-marketing",
    metaTitle:
      "Restaurant AI Automation — Reservations, Ordering & Review AI | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for restaurants & food businesses: reservation handling, AI ordering, FAQ answering, win-back offers and review automation. Free audit.",
    heroEyebrow: "Restaurant AI Automation",
    heroHeadline: "Fill Tables and Take Orders — Without Tying Up Staff",
    heroAccent: "More covers. Fewer missed calls.",
    heroTagline:
      "We deploy AI that handles reservations, answers questions, takes and confirms orders and brings guests back — so your team focuses on service, not the phone.",
    heroStats: [
      { value: "24/7", label: "reservations & enquiries" },
      { value: "−45%", label: "missed booking calls" },
      { value: "+27%", label: "repeat-visit rate" },
    ],
    insightStats: [
      {
        value: "30%",
        label: "of calls go unanswered at peak service",
        desc: "Every missed call during a rush is a lost booking. AI answers and books instantly while staff serve guests.",
      },
      {
        value: "5×",
        label: "cheaper to bring a guest back than find a new one",
        desc: "AI win-back offers turn one-time diners into regulars at a fraction of acquisition cost.",
      },
      {
        value: "70%",
        label: "of guests check reviews first",
        desc: "Automated review collection keeps your rating high and your tables full.",
      },
    ],
    automations: [
      {
        title: "AI Reservation Agent",
        body: "Takes, confirms and modifies bookings 24/7 across phone, web, WhatsApp and Instagram, synced to your reservation system.",
      },
      {
        title: "AI Ordering & FAQ",
        body: "Answers menu, allergen, hours and location questions and can take takeaway/catering orders without staff time.",
      },
      {
        title: "No-Show & Waitlist Automation",
        body: "Confirmation reminders cut no-shows, and a smart waitlist fills cancelled tables automatically.",
      },
      {
        title: "Guest Win-Back & Loyalty",
        body: "Detects lapsed guests and sends personalised offers, birthday treats and event invites to drive repeat visits.",
      },
      {
        title: "Event & Catering Lead Capture",
        body: "Captures and qualifies private-event and catering enquiries instantly, booking tastings and consultations.",
      },
      {
        title: "Review & Reputation Engine",
        body: "Requests reviews from happy diners at the right moment and routes complaints privately to protect your rating.",
      },
    ],
    stack: [
      "OpenTable / Resy / SevenRooms",
      "POS integration",
      "WhatsApp / Instagram DM",
      "OpenAI / Claude",
      "Review platforms",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "+27%",
      metricLabel: "increase in repeat-visit rate",
      badge: "Restaurant • Multi-site • Reservation + Win-Back AI",
      title: "Restaurant group filled more tables and grew repeat visits 27%.",
      body: "A restaurant group was missing booking calls during service and rarely bringing guests back. We deployed an AI reservation agent across phone, web and social plus an automated win-back and review engine. Missed booking calls fell 45%, repeat-visit rate rose 27%, and the team stayed focused on guests instead of the phone.",
    },
    faq: [
      {
        q: "Does it integrate with OpenTable or Resy?",
        a: "Yes — we connect to the major reservation systems so bookings, modifications and waitlists stay in sync.",
      },
      {
        q: "Can it take phone reservations?",
        a: "Yes — an AI voice agent can answer and book over the phone, so calls during peak service are never missed.",
      },
      {
        q: "How does it bring guests back?",
        a: "It detects lapsed diners and sends personalised offers, birthday messages and event invites that drive repeat visits.",
      },
    ],
  },

  /* ───────────────────────── TRAVEL & HOSPITALITY ───────────────────────── */
  {
    aiSlug: "travel-ai-automation",
    industrySlug: "travel-hospitality-marketing",
    metaTitle:
      "Travel & Hospitality AI Automation — Booking, Concierge & Upsell AI | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for travel & hospitality: 24/7 booking enquiries, AI concierge, upsell automation, multilingual support and review collection. Free audit.",
    heroEyebrow: "Travel & Hospitality AI Automation",
    heroHeadline: "A 24/7 Multilingual Concierge That Books and Upsells",
    heroAccent: "More direct bookings. Higher guest spend.",
    heroTagline:
      "We deploy AI that answers booking enquiries instantly in any language, acts as a digital concierge, upsells rooms and experiences and collects reviews — driving direct bookings over costly OTAs.",
    heroStats: [
      { value: "24/7", label: "multilingual support" },
      { value: "+22%", label: "direct-booking share" },
      { value: "+18%", label: "ancillary revenue" },
    ],
    insightStats: [
      {
        value: "15–25%",
        label: "OTA commission per booking",
        desc: "Every OTA booking erodes margin. AI that converts direct enquiries protects your revenue.",
      },
      {
        value: "60%",
        label: "of travel research happens after hours",
        desc: "Guests plan in the evening, often across time zones. A 24/7 multilingual AI captures them all.",
      },
      {
        value: "+18%",
        label: "revenue from upsells",
        desc: "Rooms, experiences and add-ons sell themselves when an AI concierge recommends them at the right moment.",
      },
    ],
    automations: [
      {
        title: "AI Booking & Enquiry Agent",
        body: "Answers availability, rate and policy questions instantly in multiple languages, driving direct bookings over OTAs.",
      },
      {
        title: "Digital Concierge",
        body: "A 24/7 assistant for guests pre-arrival and on-site — local tips, requests, late checkout and more — lifting satisfaction.",
      },
      {
        title: "Upsell & Experience AI",
        body: "Recommends room upgrades, experiences, dining and packages at the right moment to grow ancillary revenue.",
      },
      {
        title: "Pre-Arrival & Check-in Automation",
        body: "Sends arrival details, collects preferences and streamlines check-in so guests feel cared for before they arrive.",
      },
      {
        title: "Win-Back & Loyalty",
        body: "Re-engages past guests with personalised offers and loyalty perks to drive repeat stays directly.",
      },
      {
        title: "Review & Reputation Engine",
        body: "Requests reviews at the perfect post-stay moment and routes issues privately to protect your rating.",
      },
    ],
    stack: [
      "PMS / channel manager",
      "Booking engine",
      "WhatsApp / multilingual chat",
      "OpenAI / Claude",
      "Review platforms",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "+22%",
      metricLabel: "increase in direct-booking share",
      badge: "Hospitality • Hotel group • Concierge + Booking AI",
      title: "Hotel group grew direct bookings 22% and ancillary revenue 18%.",
      body: "A boutique hotel group leaned heavily on OTAs and missed after-hours, multilingual enquiries. We deployed a 24/7 AI booking agent and concierge with upsell flows. Direct-booking share rose 22%, ancillary revenue grew 18% from well-timed upgrade and experience offers, and guest satisfaction climbed.",
    },
    faq: [
      {
        q: "Can it handle multiple languages?",
        a: "Yes — the AI converses fluently in many languages, capturing international guests who'd otherwise book through an OTA.",
      },
      {
        q: "Does it integrate with our PMS and booking engine?",
        a: "Yes — we connect to your property-management system and booking engine so availability and rates are always accurate.",
      },
      {
        q: "How does it increase direct bookings?",
        a: "By answering instantly and converting enquiries on your own channels, it reduces reliance on commission-charging OTAs.",
      },
    ],
  },

  /* ───────────────────────── LAW FIRMS & LEGAL ───────────────────────── */
  {
    aiSlug: "law-firm-ai-automation",
    industrySlug: "law-firm-marketing",
    metaTitle:
      "Law Firm AI Automation — Client Intake, Qualification & Booking | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for law firms: instant client intake, case qualification, consultation booking and nurture — so no qualified lead is ever lost. Free audit.",
    heroEyebrow: "Law Firm AI Automation",
    heroHeadline: "Capture and Qualify Every Case — Around the Clock",
    heroAccent: "More signed clients. No leads lost.",
    heroTagline:
      "We deploy AI that responds to enquiries instantly, runs structured intake, qualifies case fit and books consultations — so your firm converts more of the leads you're already paying for.",
    heroStats: [
      { value: "30 sec", label: "intake response time" },
      { value: "+41%", label: "consult booking rate" },
      { value: "24/7", label: "client intake" },
    ],
    insightStats: [
      {
        value: "$500+",
        label: "average cost per legal lead",
        desc: "Legal leads are expensive. Letting one go unanswered after hours is costly — AI captures every one.",
      },
      {
        value: "42%",
        label: "of law-firm calls go unanswered",
        desc: "Missed calls are missed cases. An AI intake agent answers and qualifies instantly, day or night.",
      },
      {
        value: "5 min",
        label: "window before a lead calls a competitor",
        desc: "Speed wins cases. Instant AI response keeps prospects from shopping other firms.",
      },
    ],
    automations: [
      {
        title: "Instant Intake AI",
        body: "Responds in seconds to web, ad and call enquiries, gathering case details through a structured, compliant conversation.",
      },
      {
        title: "Case Qualification & Routing",
        body: "Scores case fit, jurisdiction and urgency, then routes qualified matters to the right attorney and flags conflicts.",
      },
      {
        title: "Consultation Booking",
        body: "Books qualified prospects into the right attorney's calendar with reminders that maximise show-up rate.",
      },
      {
        title: "Lead Nurture AI",
        body: "Keeps not-yet-ready prospects engaged with relevant information until they're ready to proceed.",
      },
      {
        title: "Document & Form Collection",
        body: "Guides clients to securely submit the documents and information needed to open a matter — cutting back-and-forth.",
      },
      {
        title: "Review & Referral Engine",
        body: "Requests reviews from satisfied clients and encourages referrals at the right point in the matter.",
      },
    ],
    stack: [
      "Clio / legal CRM",
      "Calendar sync",
      "Secure intake forms",
      "WhatsApp / SMS",
      "OpenAI / Claude",
      "DocuSign",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "+41%",
      metricLabel: "increase in consultations booked",
      badge: "Legal • Firm • Intake + Qualification AI",
      title: "Law firm booked 41% more consults from existing lead spend.",
      body: "A firm was paying premium rates for leads but losing after-hours enquiries and converting too few to consultations. We deployed instant AI intake with case qualification and calendar booking. Consultations booked rose 41%, no qualified lead went unanswered, and attorneys only spent time on matters that fit.",
    },
    faq: [
      {
        q: "Does the AI give legal advice?",
        a: "No. It handles intake, qualification, booking and document collection only. All legal advice stays with your attorneys — the AI removes the admin around it.",
      },
      {
        q: "Is intake data confidential and compliant?",
        a: "Yes — encrypted, access-controlled intake designed around legal confidentiality and your jurisdiction's requirements, with conflict flagging.",
      },
      {
        q: "Does it integrate with Clio or our CRM?",
        a: "Yes — qualified leads, details and documents flow into Clio or your legal CRM automatically.",
      },
    ],
  },

  /* ───────────────────────── FINANCIAL ADVISORS & WEALTH ───────────────────────── */
  {
    aiSlug: "financial-advisor-ai-automation",
    industrySlug: "financial-advisor-marketing",
    metaTitle:
      "Financial Advisor AI Automation — Lead Qualification & Booking | Lumotis",
    metaDescription:
      "Lumotis builds compliant AI automation for financial advisors & wealth firms: lead qualification, discovery-call booking, nurture and review automation. Free audit.",
    heroEyebrow: "Financial Advisor AI Automation",
    heroHeadline: "Fill Your Calendar with Qualified Prospects",
    heroAccent: "More discovery calls. Compliant. Hands-off.",
    heroTagline:
      "We deploy compliant AI that qualifies enquiries, books discovery calls, nurtures long-consideration prospects and requests reviews — so advisors spend time with qualified clients, not chasing leads.",
    heroStats: [
      { value: "30 sec", label: "lead-response time" },
      { value: "+39%", label: "discovery-call booking" },
      { value: "24/7", label: "enquiry handling" },
    ],
    insightStats: [
      {
        value: "$1M+",
        label: "lifetime value of a single client",
        desc: "With this much at stake, a missed enquiry is enormously costly. AI captures and qualifies every one.",
      },
      {
        value: "3–6 mo",
        label: "typical decision window",
        desc: "Choosing an advisor takes time. AI nurture keeps your firm front-of-mind through the whole journey.",
      },
      {
        value: "70%",
        label: "of advisor time lost to non-advisory tasks",
        desc: "Qualification and scheduling drain billable time. Automating them lets advisors focus on advice.",
      },
    ],
    automations: [
      {
        title: "Compliant Lead Qualification",
        body: "Responds instantly and qualifies prospects on assets, goals and fit using scripted, compliant conversations.",
      },
      {
        title: "Discovery-Call Booking",
        body: "Books qualified prospects into the right advisor's calendar with reminders that maximise attendance.",
      },
      {
        title: "Long-Window Nurture AI",
        body: "Keeps considering prospects engaged with relevant education and check-ins across the multi-month decision window.",
      },
      {
        title: "Client Onboarding Automation",
        body: "Guides new clients through document collection and onboarding steps securely — reducing admin and time-to-active.",
      },
      {
        title: "Review & Referral Engine",
        body: "Requests reviews and referrals at the right moments, building the trust that drives high-value enquiries.",
      },
      {
        title: "Reactivation Triggers",
        body: "Re-engages dormant prospects and past enquiries around life events and market moments that signal readiness.",
      },
    ],
    stack: [
      "CRM (Salesforce / Redtail)",
      "Calendar sync",
      "Secure forms / e-sign",
      "WhatsApp / SMS",
      "OpenAI / Claude",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "+39%",
      metricLabel: "increase in discovery calls booked",
      badge: "Wealth • Advisory firm • Qualification + Nurture AI",
      title: "Advisory firm booked 39% more discovery calls — compliantly.",
      body: "A wealth firm was losing high-value enquiries to slow follow-up and weak nurture. We deployed compliant AI qualification, discovery-call booking and a long-window nurture track. Discovery calls rose 39%, advisors only met qualified prospects, and considering leads stayed engaged across the multi-month decision window.",
    },
    faq: [
      {
        q: "Does the AI give financial advice?",
        a: "No. It qualifies, books and nurtures using compliant, scripted conversations. All regulated advice stays with your licensed advisors.",
      },
      {
        q: "Is it compliant for a regulated industry?",
        a: "Yes — we design the conversation flows and disclosures around your regulatory requirements and keep records auditable.",
      },
      {
        q: "Does it integrate with our CRM?",
        a: "Yes — qualified leads and details flow into Salesforce, Redtail or your CRM automatically.",
      },
    ],
  },

  /* ───────────────────────── ACCOUNTANTS & CPAs ───────────────────────── */
  {
    aiSlug: "accounting-ai-automation",
    industrySlug: "accounting-marketing",
    metaTitle:
      "Accounting AI Automation — Client Onboarding, Doc Collection & Reminders | Lumotis",
    metaDescription:
      "Lumotis builds AI automation for accountants & CPAs: lead qualification, client onboarding, document collection, deadline reminders and review automation. Free audit.",
    heroEyebrow: "Accounting AI Automation",
    heroHeadline: "Onboard Clients and Collect Documents — On Autopilot",
    heroAccent: "Less admin. Smoother season. More capacity.",
    heroTagline:
      "We deploy AI that qualifies enquiries, onboards new clients, chases the documents you need and sends deadline reminders — so your team stops chasing paperwork and focuses on billable work.",
    heroStats: [
      { value: "−55%", label: "onboarding admin time" },
      { value: "24/7", label: "enquiry handling" },
      { value: "+30%", label: "on-time document submission" },
    ],
    insightStats: [
      {
        value: "60%",
        label: "of firm time lost to admin & chasing",
        desc: "Onboarding and document chasing consume billable hours. Automating them expands capacity without hiring.",
      },
      {
        value: "Tax season",
        label: "is a capacity bottleneck",
        desc: "AI handles intake, onboarding and reminders at scale, so peak season stops overwhelming your team.",
      },
      {
        value: "30%",
        label: "more on-time document submission",
        desc: "Automated, persistent (but polite) reminders get clients to send documents before deadlines.",
      },
    ],
    automations: [
      {
        title: "Lead Qualification AI",
        body: "Responds instantly to enquiries, qualifies service needs and entity type, and books discovery calls with the right person.",
      },
      {
        title: "Client Onboarding Automation",
        body: "Guides new clients through engagement steps, e-signatures and account setup — slashing onboarding admin.",
      },
      {
        title: "Document Collection & Chasing",
        body: "Requests, reminds and securely collects tax documents, receipts and statements so your team stops chasing.",
      },
      {
        title: "Deadline & Filing Reminders",
        body: "Sends automated reminders for tax deadlines, document due-dates and filings — reducing late submissions and penalties.",
      },
      {
        title: "FAQ & Status Agent",
        body: "Answers common questions on services, pricing, deadlines and 'where's my return?' status instantly, any time.",
      },
      {
        title: "Review & Referral Engine",
        body: "Requests reviews and referrals after a smooth filing — the best moment to grow your reputation and pipeline.",
      },
    ],
    stack: [
      "Practice CRM",
      "Secure doc portal",
      "Calendar sync",
      "E-signature",
      "OpenAI / Claude",
      "Email / SMS",
      "Zapier / Make",
    ],
    caseStudy: {
      metric: "−55%",
      metricLabel: "reduction in onboarding admin time",
      badge: "Accounting • CPA firm • Onboarding + Doc AI",
      title: "CPA firm cut onboarding admin by half and smoothed tax season.",
      body: "A CPA firm was buried in onboarding paperwork and document chasing every season. We deployed AI onboarding with e-signature plus automated, secure document collection and deadline reminders. Onboarding admin fell 55%, on-time document submission rose 30%, and the team handled peak season without extra hires.",
    },
    faq: [
      {
        q: "Is client financial-document handling secure?",
        a: "Yes — documents are collected through encrypted, access-controlled portals, and the AI follows your data-handling and compliance requirements.",
      },
      {
        q: "Can it handle the tax-season surge?",
        a: "Yes — that's where it pays off most. It scales intake, onboarding and reminders so peak season stops overwhelming your team.",
      },
      {
        q: "Does it integrate with our practice software?",
        a: "Yes — we connect to your CRM, document portal and e-signature tools so everything flows automatically.",
      },
    ],
  },
];

export function getAiAutomationBySlug(slug) {
  return AI_AUTOMATION_LIST.find((a) => a.aiSlug === slug) || null;
}

// All page slugs — used for route generation / sitemap.
export const AI_AUTOMATION_SLUGS = AI_AUTOMATION_LIST.map((a) => a.aiSlug);
